type BrowserSessionOrigins = Readonly<Record<string, string>>

const localHosts = new Set(["localhost", "127.0.0.1", "[::1]"])

const neloSites = ["nelo.mx", "nelo.co"]

const neloSite = (host: string): string | undefined => neloSites.find((site) => host === site || host.endsWith(`.${site}`))

const exactOrigin = (value: string): URL => {
  const url = new URL(value)
  if (url.origin !== value || url.hostname.includes("*") || (url.protocol !== "https:" && !(url.protocol === "http:" && localHosts.has(url.hostname)))) {
    throw new Error(`Invalid browser session origin: ${value}`)
  }
  return url
}

export const validateBrowserSessionOrigins = (origins: BrowserSessionOrigins): BrowserSessionOrigins => {
  for (const [pageOrigin, apiOrigin] of Object.entries(origins)) {
    const page = exactOrigin(pageOrigin)
    const api = exactOrigin(apiOrigin)
    const site = neloSite(page.hostname)
    if (page.protocol !== api.protocol || (page.hostname !== api.hostname && !(site && site === neloSite(api.hostname)))) {
      throw new Error(`Browser session requires same-site origins: ${pageOrigin} -> ${apiOrigin}`)
    }
  }
  return origins
}

const configuredOrigins = (value: string | undefined): BrowserSessionOrigins => {
  if (!value) return {}
  const parsed: unknown = JSON.parse(value)
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed) || Object.values(parsed).some((origin) => typeof origin !== "string")) {
    throw new Error("Browser session origins must be an object mapping exact page origins to API origins")
  }
  return validateBrowserSessionOrigins(parsed as Record<string, string>)
}

export const browserSessionOrigins = validateBrowserSessionOrigins({
  "https://tienda.nelo.mx": "https://api.tienda.nelo.mx",
  "https://checkout.nelo.co": "https://api.checkout.nelo.co",
  "https://checkout-sandbox.nelo.co": "https://api.checkout-sandbox.nelo.co",
  ...(process.env.NODE_ENV === "production" ? {} : { "http://localhost:8000": "http://localhost:9000", "http://127.0.0.1:8000": "http://127.0.0.1:9000" }),
  ...configuredOrigins(process.env.NEXT_PUBLIC_BROWSER_SESSION_ORIGINS),
})

export const browserSessionOrigin = (pageOrigin: string, origins: BrowserSessionOrigins = browserSessionOrigins): string | undefined =>
  Object.hasOwn(origins, pageOrigin) ? origins[pageOrigin] : undefined

export const sharesSessionCookie = (pageOrigin: string, origins: BrowserSessionOrigins = browserSessionOrigins): boolean => {
  const apiOrigin = browserSessionOrigin(pageOrigin, origins)
  if (apiOrigin === undefined) return false
  const page = new URL(pageOrigin).hostname
  const api = new URL(apiOrigin).hostname
  return api === page || api.endsWith(`.${page}`)
}
